import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Brain, Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import FlashcardDeck from "@/components/FlashcardDeck";

/**
 * FlashcardsDailyCard — Phase 4 / Iter75.
 *
 * Surfaces the user's daily review status on the Hub. Self-hides when
 * the user has zero cards in their library (the empty state would be
 * confusing as a permanent dashboard fixture — discovery happens via
 * the in-lesson "Make flashcards" CTA instead).
 *
 * Three states:
 *   1. No cards (total_cards = 0)         → render null
 *   2. Cards due / new today              → "Review N cards" CTA
 *   3. Daily completed (or nothing due)   → "All done · come back tomorrow"
 */
export default function FlashcardsDailyCard() {
  const [snapshot, setSnapshot] = useState(null); // {size, total_cards, daily_completed, reward_credits}
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/flashcards/daily");
      setSnapshot(data);
    } catch {
      setSnapshot(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    // Hub-wide refresh on auth change so the widget reflects the
    // logged-in user (we don't pass user_id around — it's session-based).
    const onAuth = () => refresh();
    window.addEventListener("oak:auth-changed", onAuth);
    return () => window.removeEventListener("oak:auth-changed", onAuth);
  }, []);

  if (loading) {
    return (
      <div className="glass p-5 flex items-center gap-3" data-testid="flashcards-daily-loading">
        <Loader2 size={14} className="text-white/40 animate-spin" />
        <div className="text-white/55 text-xs">Loading review deck…</div>
      </div>
    );
  }

  if (!snapshot || (snapshot.total_cards || 0) === 0) {
    // First-run users: don't show a dead card — discovery is in-lesson.
    return null;
  }

  const due = snapshot.size || 0;
  const completed = !!snapshot.daily_completed;
  const allCaughtUp = due === 0;

  return (
    <>
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="glass p-5 sm:p-6 relative overflow-hidden"
        data-testid="flashcards-daily-card"
      >
        {/* Decorative gradient blob — keeps the card from feeling flat. */}
        <div
          className="absolute -top-12 -right-12 h-44 w-44 rounded-full bg-[#0044FF]/12 blur-3xl pointer-events-none"
          aria-hidden
        />
        <div className="relative flex items-start gap-4">
          <div className="h-11 w-11 grid place-items-center rounded-xl bg-[#0044FF]/12 border border-[#0044FF]/35 shrink-0">
            <Brain size={18} className="text-[#9bc7ff]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow text-[#9bc7ff] mb-1 flex items-center gap-1.5">
              Daily Flashcards
              {completed && (
                <span className="inline-flex items-center gap-1 text-[#9bffcc] text-[10px]">
                  <CheckCircle2 size={10} /> Done today
                </span>
              )}
            </div>
            <h3 className="font-display text-lg sm:text-xl text-white tracking-tight leading-snug">
              {completed
                ? "You've reviewed today's deck"
                : allCaughtUp
                  ? "All caught up · no cards due"
                  : `${due} card${due === 1 ? "" : "s"} ready for review`}
            </h3>
            <p className="text-[12.5px] text-white/55 mt-1 leading-relaxed">
              {completed
                ? "Come back tomorrow for the next batch — your brain is still consolidating."
                : allCaughtUp
                  ? `${snapshot.total_cards} cards in your library · the next due round will appear here.`
                  : <>Spaced-repetition keeps facts long-term. Tap a rating after each card — earn <span className="text-[#9bffcc]">+{snapshot.xp_per_card || 5} XP</span> each and a <span className="text-[#9bffcc]">+{snapshot.reward_credits || 1} credit</span> for finishing the deck.</>}
            </p>
            <div className="mt-3 flex items-center gap-3">
              <button
                onClick={() => setOpen(true)}
                disabled={completed || allCaughtUp}
                className={`text-[12px] font-display font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5 transition ${
                  completed || allCaughtUp
                    ? "border border-white/10 bg-white/[0.03] text-white/40 cursor-not-allowed"
                    : "btn-primary text-white"
                }`}
                data-testid="flashcards-daily-start"
              >
                {completed ? "Reviewed" : allCaughtUp ? "Nothing due" : "Start review"}
                {!completed && !allCaughtUp && <ArrowRight size={12} />}
              </button>
              <span className="text-[10.5px] text-white/40 font-mono-oak">
                {snapshot.total_cards} card{snapshot.total_cards === 1 ? "" : "s"} in library
              </span>
            </div>
          </div>
        </div>
      </motion.section>
      <FlashcardDeck
        open={open}
        onOpenChange={setOpen}
        mode="daily"
        onComplete={refresh}
      />
    </>
  );
}
